import 'babel-polyfill'
import './polyfills'

import posthog from 'posthog-js'

import Vue from 'vue'
import Router from 'vue-router'
import Raven from 'raven-js'
import RavenVue from 'raven-js/plugins/vue'
import VueAnalytics from 'vue-analytics'

import App from './App'
import BasicHome from './components/BasicHome'
import Manage from './components/Manage'
import Files from './components/Files'
import VideoByID from './components/VideoByID'
import VideoBySlug from './components/VideoBySlug'
import HTTP404 from './components/HTTP404'
import Team from './components/Team'
import MyGroups from './components/MyGroups'
import FindGroups from './components/FindGroups'
import GroupCreate from './components/GroupCreate'
import Settings from './components/Settings'
import TaggedComments from './components/TaggedComments'
import Login from './components/Login'
import Signup from './components/Signup'
import MagicLogin from './components/MagicLogin'
import ChangePassword from './components/ChangePassword'
import ConfirmPasswordReset from './components/ConfirmPasswordReset'
import ForgotPassword from './components/ForgotPassword'
import Library from './components/Library'
import Folder from './components/Folder'
import ResendEmailConfirmation from './components/ResendEmailConfirmation'
import ConfirmEmail from './components/ConfirmEmail'
import RequestInfo from './components/RequestInfo'
import RequestDemo from './components/RequestDemo'
import Activate from './components/Activate'
import MyColleagues from './components/MyColleagues'
import FindColleauges from './components/FindColleagues'
import OAuthLoginCallback from './components/OAuthLoginCallback'
import Tags from './components/Tags'
import Exercise from './components/Exercise'

import { GOOGLE_ANALYTICS_ID, SENTRY_URL } from './settings'

import store from './store'

const isDebug = process.env.NODE_ENV === 'development'
if (!isDebug) {
  Raven
    .config(SENTRY_URL)
    .addPlugin(RavenVue, Vue)
    .install()
}

// prevent the production tip on Vue startup
Vue.config.productionTip = false

Vue.use(Router)

import axios from 'axios'

const anonRequired = (_, __, next) => {
  axios.get('/api/v1/rest-auth/user/', {
    headers: { 'Authorization': 'Token ' + localStorage.getItem('token') },
  })
  .then(() => {
    next('/')
  })
  .catch(() => next())
}

const loginRequired = (_, __, next) => {
  store.dispatch('isAuthenticated')
  .then(() => next())
  .catch(() => next('/login'))
}

const emailConfirmed = (_, __, next) => {
  store.dispatch('isAuthenticated')
  .then(() => {
    const isConfirmed = store.state.user.email_confirmed
    if (isConfirmed) {
      next()
    } else {
      next('/resend-email-confirmation')
    }
  })
  .catch(() => next('/login'))
}

const canCreateUsers = (to, from, next) => {
  const u = store.state.user
  if (u.is_group_admin || u.is_coach) {
    emailConfirmed(to, from, next)
  } else {
    next('/404')
  }
}

export const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/',
      component: BasicHome,
      beforeEnter: emailConfirmed,
    },
    {
      path: '/manage',
      component: Manage,
      beforeEnter: emailConfirmed,
    },
    {
      path: '/settings',
      component: Settings,
      beforeEnter: loginRequired,
    },
    {
      path: '/files',
      component: Files,
      beforeEnter: emailConfirmed,
    },
    {
      path: '/videos/:id',
      component: VideoByID,
      props: route => ({ id: parseInt(route.params.id, 10) }),
      beforeEnter: emailConfirmed,
    },
    {
      path: '/v/:slug',
      component: VideoBySlug,
      props: route => ({ slug: route.params.slug }),
      beforeEnter: emailConfirmed,
    },
    {
      path: '/groups/:id',
      component: Team,
      beforeEnter: emailConfirmed,
    },
    {
      path: '/groups',
      component: MyGroups,
      beforeEnter: emailConfirmed,
    },
    {
      path: '/tags',
      component: Tags,
      beforeEnter: emailConfirmed,
    },
    {
      path: '/group-find',
      component: FindGroups,
      beforeEnter: emailConfirmed,
    },
    {
      path: '/group-create',
      component: GroupCreate,
      beforeEnter: canCreateUsers,
    },
    {
      path: '/folders/:id',
      component: Folder,
      props: route => ({ id: parseInt(route.params.id, 10) }),
      beforeEnter: emailConfirmed,
    },
    {
      path: '/libraries/:id',
      component: Library,
      beforeEnter: emailConfirmed,
    },
    {
      path: '/colleagues',
      component: MyColleagues,
      beforeEnter: emailConfirmed,
    },
    {
      path: '/colleagues/find',
      component: FindColleauges,
      beforeEnter: emailConfirmed,
    },
    {
      path: '/tags/:id',
      component: TaggedComments,
      props: route => ({ id: parseInt(route.params.id, 10) }),
      beforeEnter: emailConfirmed,
    },
    {
      path: '/exercise',
      component: Exercise,
      beforeEnter: emailConfirmed,
    },
    {
      path: '/login',
      component: Login,
    },
    {
      path: '/accounts/:provider/login/callback/',
      component: OAuthLoginCallback,
      props: route => ({
        provider: route.params.provider,
        code: route.query.code || route.query.token,
        state: route.query.state,
        type: route.query.state.split('_')[1] === 'login'
          ? 'login'
          : 'signup'
      }),
    },
    {
      path: '/accounts/:provider/connect/',
      component: OAuthLoginCallback,
      props: route => ({
        provider: route.params.provider,
        code: route.query.code || route.query.token,
        state: route.query.state,
        type: 'connect',
      }),
    },
    {
      path: '/resend-email-confirmation',
      component: ResendEmailConfirmation,
      beforeEnter: loginRequired,
    },
    {
      path: '/confirm-email/:key',
      component: ConfirmEmail,
      props: route => ({ key: route.params.key }),
    },
    {
      path: '/Signup',
      component: Signup,
      beforeEnter: anonRequired,
    },
    {
      path: '/requestdemo',
      component: RequestDemo,
      beforeEnter: anonRequired,
    },
    {
      path: '/requestinfo',
      component: RequestInfo,
      beforeEnter: anonRequired,
    },
    {
      path: '/magic/:magicToken',
      component: MagicLogin,
      props: route => ({ magicToken: route.params.magicToken }),
      beforeEnter: anonRequired,
    },
    {
      path: '/activate',
      component: Activate,
      beforeEnter: anonRequired,
    },
    {
      path: '/change-password',
      component: ChangePassword,
      beforeEnter: loginRequired,
    },
    {
      path: '/set-password',
      component: ChangePassword,
      props: {
        setPassword: true,
      },
      beforeEnter: loginRequired,
    },
    {
      path: '/forgot-password',
      component: ForgotPassword,
    },
    {
      path: '/reset/confirm/:uid-:token',
      component: ConfirmPasswordReset,
    },
    {
      path: '/*',
      component: HTTP404,
    },
  ],
})

if (!isDebug) {
  Vue.use(VueAnalytics, {
    id: GOOGLE_ANALYTICS_ID,
    router,
  })

  posthog.init('9qxF28BQG4KwFZrvaL7zTS7NILUq5adrx9SXuuHlRE0', { api_host: 'https://posthog.classforward.com' })
}

/* eslint-disable no-new */
new Vue({
  el: '#app',
  router,
  store,
  template: '<App/>',
  components: { App },
})



// WEBPACK FOOTER //
// ./src/main.js